import React, { useEffect, useState } from "react";
import { CarrierManagementConstants } from '../../../shared/util/constant';
import { CarrierDataModel } from "../Model/CarrierDataModel";
import CarrierManagementAPI from "../Services/CarrierManagementAPI";
import { Icon, Spinner } from "../../../components";
import { PanelMenu } from "primereact/panelmenu";
import './CarrierManagementPanel.scss';

interface Props {
    onSelectCarrier: (id: string, parentDetail?: any) => void;
    selectedCarrierId: string | null;
    onParentSelect?: (parentId: string | null) => void;
    onAddParentCompany?: () => void;
    canCreate?: boolean;
}

const CarrierManagementPanel: React.FC<Props & { refresh?: number, onRefreshCarriers?: (carriers: any[]) => void }> = ({ onSelectCarrier, selectedCarrierId, refresh, onParentSelect, onAddParentCompany, onRefreshCarriers, canCreate = true }) => {
    const [loading, setLoading] = useState(false);
    const [menuItems, setMenuItems] = useState<any[]>([]);
    const [allCarriers, setAllCarriers] = useState<CarrierDataModel[]>([]);
    const [searchValue, setSearchValue] = useState("");

    useEffect(() => {
        fetchCarriers();
    }, [refresh]);

    const fetchCarriers = async () => {
        setLoading(true);
        const api = new CarrierManagementAPI();
        const data = await api.getCarriers();
        setAllCarriers(data);
        setMenuItems(buildMenuItems(data));
        if (onRefreshCarriers) onRefreshCarriers(data);
        setLoading(false);
    };

    const buildMenuItems = (carriers: CarrierDataModel[]) => {
        const grouped: { [parent: string]: CarrierDataModel[] } = {};
        carriers.forEach(carrier => {
            const parent = carrier.parentCompanyName || CarrierManagementConstants.OTHER_PARENT_LABEL;
            if (!grouped[parent]) grouped[parent] = [];
            grouped[parent].push(carrier);
        });
        return Object.entries(grouped).map(([parentName, children]) => {
            
            const parentCarrier = children.find(child => child.name === parentName);
            const isParentSelected = parentCarrier && parentCarrier.id === selectedCarrierId;
            const isChildSelected = children.some(child => child.id === selectedCarrierId);
            return {
                label: parentName,
                expanded: isParentSelected || isChildSelected,
                command: () => {
                    if (parentCarrier) {
                        onSelectCarrier(parentCarrier.id, parentCarrier);
                        if (onParentSelect) onParentSelect(parentCarrier.id);
                    } else {
                        onSelectCarrier(null);
                        if (onParentSelect) onParentSelect(null);
                    }
                },
                items: children.filter(child => child.name !== parentName)
                    .map(child => ({
                    label: child.name,
                    id: child.id,
                    template: (_item, _options) => (
                        <div
                            id={child.id}
                            className={`flex align-items-center px-2 py-2 cursor-pointer${selectedCarrierId === child.id ? " active-child-link" : ""}`}
                            onClick={async e => {
                                e.preventDefault();
                                await onSelectCarrier(child.id, parentCarrier);
                                if (onParentSelect) onParentSelect(children.find(child2 => child2.name === parentName)?.id || null);
                                
                                document.querySelectorAll('.active-child-link').forEach(el => {
                                    if (el.id !== child.id) el.classList.remove('active-child-link');
                                });


                                // On Selection on child remove highlight from parent
                                if (parentCarrier?.name) {
                                    const parentHeader = document.querySelector(
                                        `div[aria-label='${parentCarrier.name}'][aria-expanded='true']`
                                    );
                                    if (parentHeader) {
                                        parentHeader.setAttribute('aria-expanded', 'false');
                                    }
                                }                                
                            }}
                        >
                            {child.name}
                        </div>
                    )
                })),
                template: (_item, _options) => (
                         <div id={"header_"+parentName} className="p-panelmenu-header-content headerTemplate" data-pc-section="headercontent">
                                <a href="#" className="p-panelmenu-header-link" data-pc-section="headeraction">
                                <Icon icon={`${isParentSelected || isChildSelected ? 'down-arrow' : 'right-arrow'}`}></Icon>
                                  <span className="p-menuitem-text" data-pc-section="headerlabel" style={{ fontFamily: "Roboto Condensed, sans-serif" }}>
                                    <span title={parentName} className="truncate">{parentName}</span>
                                    <span className="ml-auto p-badge p-component p-badge-no-gutter">{children.length}</span>
                                  </span>
                                </a>
                              </div>
                    )
            };
        });
    };

     // Search filter logic
    const getFilteredCarriers = () => {
        if (!searchValue.trim()) return allCarriers;
        const val = searchValue.trim().toLowerCase();
        const filtered = allCarriers.filter(carrier => {
            if (carrier.naic && carrier.naic.toString() === val) return true;
            if (carrier.name && carrier.name.toLowerCase().includes(val)) return true;
            if (carrier.parentCompanyName && carrier.parentCompanyName.toLowerCase().includes(val)) return true;
            return false;
        });
        // Now, get all unique parentCompanyId for filtered children
        const parentIds = new Set(filtered.map(c => c.parentCompanyId));
        // Include all parents for those children
        return allCarriers.filter(carrier =>
            filtered.includes(carrier) ||
            parentIds.has(carrier.id)
        );
    };

    return (
        <div className="card">
        <div className="card-body" style={{ minHeight: '500px' }}>
            <div className="card-header"  style={{ background: 'white'}}>
            <div className="d-flex align-items-center justify-content-between  fw-bold fs-5">
                <span>{CarrierManagementConstants.INSURANCE_COMPANY_HEADER}</span>
                <button
                    type="button"
                    className="btn btn-outline-primary"
                    disabled={!canCreate}
                    onClick={() => {
                        if (typeof onAddParentCompany === 'function') {
                            onAddParentCompany();
                        }
                    }}
                >
                    {CarrierManagementConstants.ADD_PARENT_COMPANY_BUTTON}
                </button>
            </div>
            <div className="pt-3">
                <div className="d-flex align-items-center pb-3">
                    <div className="position-relative flex-grow-1">
                        <input
                            type="text"
                            className={`form-control pe-5 carrier-search-input${searchValue ? ' carrier-search-selected' : ''}`}
                            placeholder="Search Insurance Companies and NAIC"
                            value={searchValue}
                            onChange={e => setSearchValue(e.target.value)}
                            autoFocus
                        />
                        <span className={`carrier-search-icon${searchValue ? ' carrier-search-selected' : ''}`}> 
                            <i className="bi bi-search" />
                        </span>
                    </div>
                    {searchValue && (
                        <span
                            className="carrier-search-clear"
                            onClick={() => setSearchValue("")}
                            title="Clear search"
                        >
                            <i className="bi bi-x" />
                        </span>
                    )}
                </div>
            </div>
            </div>
            <div className="card-body p-0" style={{ height: 'calc(100vh - 65px - 75px)', display: 'flex', flexDirection: 'column' }}>
                <div className="px-3 pb-5 flex-grow-1 overflow-auto mt-3">
                    {loading ? (
                        <Spinner loading={true} type={"SyncLoader"} />
                    ) : (
                        <>
            
            <PanelMenu
                model={buildMenuItems(getFilteredCarriers())}
                className="w-full md:w-20rem"
                collapseIcon="pi pi-chevron-down"
                expandIcon="pi pi-chevron-right"
                multiple={false}
            />
                        </>
                    )}
                </div>
            </div>
        </div>
        </div>
    );
};

export default React.memo(CarrierManagementPanel);